import {
  DATA_CONSUMED_KEY, DATA_GENERATED_KEY,
} from 'src/util/constants';
import logger from 'src/util/logger';

import { getPropertyDataInBatches } from './dataBatching';

/**
 * Maximum number of cached date-range segments.
 * See docs/design-docs/property-show-caching.md
 * This will be moved to a config as part of https://enosi.atlassian.net/browse/PT-1956
 */
export const MAX_CACHE_SEGMENTS = 5;

/** Module-level cache instance. Only one PropertyShow exists at a time. */
let cache = { propertyId: null, segments: {} };

/**
 * Create a fresh cache object for a given propertyId and assign it
 * to the module-level singleton.
 * @param {string} propertyId - The property UUID.
 * @returns {object} New cache object with empty segments.
 */
export const initialiseCache = (propertyId) => {
  cache = { propertyId, segments: {} };
  return cache;
};

/**
 * Return the current module-level cache singleton. Intended for
 * read-only consumers (e.g. cache-recovery flows that peek at the cache
 * without triggering a fetch via `getPropertyDataFromCache`).
 * @returns {object} The current cache `{ propertyId, segments }`.
 */
export const getCache = () => cache;

/**
 * Find a cached segment whose date range fully contains the requested
 * dateSpan at the same aggregation level.
 * @param {object} segments - Object keyed by `startSeconds_finishSeconds`,
 *   each value having `start`, `finish` Luxon DateTime properties and
 *   `aggregation`.
 * @param {object} dateSpan - Requested range with `start` and `finish`
 *   Luxon DateTimes.
 * @param {string} aggregation - Required aggregation level
 *   (e.g. 'PT30M', 'P1D').
 * @returns {object|null} The first containing segment, or null if no
 *   segment covers the full requested range.
 */
export const getSegmentDataFromCache = (segments, dateSpan, aggregation) => {
  if (!segments || !dateSpan?.start || !dateSpan?.finish || !aggregation) {
    return null;
  }

  const requestedStart = dateSpan.start.toSeconds();
  const requestedFinish = dateSpan.finish.toSeconds();

  const keys = Object.keys(segments);
  for (let i = 0; i < keys.length; i += 1) {
    const segment = segments[keys[i]];
    if (
      segment?.start
      && segment?.finish
      && requestedStart >= segment.start.toSeconds()
      && requestedFinish <= segment.finish.toSeconds()
      && segment.aggregation === aggregation
    ) {
      return segment;
    }
  }

  return null;
};

/**
 * Extract a subset of combinedData filtered to the requested dateSpan.
 * Filters time-series arrays on each meter node without mutating the source.
 *
 * Filtered fields per meter node:
 * - `dataConsumed.data` — by `timestamp` within [startSeconds, finishSeconds]
 * - `dataGenerated.data` — same filter
 * - `tradeSetSummary[].data` — by `range.finish` within [startSeconds, finishSeconds]
 * - `rules.edges` — by overlap of each rule's `start`/`finish` with the requested span
 *   (`null` start/finish treated as unbounded); see `getFilteredRules` below
 *
 * All other fields (meter metadata, property info) are copied as-is.
 * @param {object} combinedData - Cached combinedData from a segment.
 * @param {object} dateSpan - Requested range with `start` and `finish` Luxon DateTimes.
 * @returns {object} New combinedData object scoped to the requested range.
 */
export const extractDataFromCache = (combinedData, dateSpan) => {
  if (!combinedData?.property?.meters?.edges || !dateSpan?.start || !dateSpan?.finish) {
    return combinedData;
  }

  const startSeconds = Math.floor(dateSpan.start.toSeconds());
  const finishSeconds = Math.floor(dateSpan.finish.toSeconds());

  const filteredEdges = combinedData.property.meters.edges.map((edge) => {
    const node = edge?.node;
    if (!node) return edge;

    const getFilteredTimeSeries = (dataObj) => {
      if (!dataObj?.data) return dataObj;
      return {
        ...dataObj,
        data: dataObj.data.filter(
          (point) => point.timestamp >= startSeconds && point.timestamp <= finishSeconds,
        ),
      };
    };

    const getFilteredTradeSummary = (summaryArray) => {
      if (!Array.isArray(summaryArray)) return summaryArray;
      return summaryArray.map((trade) => {
        if (!Array.isArray(trade?.data)) return trade;
        return {
          ...trade,
          data: trade.data.filter(
            (point) => point?.range?.finish >= startSeconds
              && point?.range?.finish <= finishSeconds,
          ),
        };
      });
    };

    const getFilteredRules = (rulesObj) => {
      if (!rulesObj?.edges) return rulesObj;
      return {
        ...rulesObj,
        edges: rulesObj.edges.filter((ruleEdge) => {
          if (!ruleEdge?.node) return false;
          const { start: ruleStart, finish: ruleFinish } = ruleEdge.node;
          // Rule overlaps if it starts before requested finish AND finishes after requested start.
          // null start is treated as beginning of time; null finish is treated as end of time.
          const overlapsStart = !ruleFinish || ruleFinish >= startSeconds;
          const overlapsFinish = !ruleStart || ruleStart <= finishSeconds;
          return overlapsStart && overlapsFinish;
        }),
      };
    };

    return {
      node: {
        ...node,
        [DATA_CONSUMED_KEY]: getFilteredTimeSeries(node[DATA_CONSUMED_KEY]),
        [DATA_GENERATED_KEY]: getFilteredTimeSeries(node[DATA_GENERATED_KEY]),
        tradeSetSummary: getFilteredTradeSummary(node.tradeSetSummary),
        rules: getFilteredRules(node.rules),
      },
    };
  });

  return {
    ...combinedData,
    property: {
      ...combinedData.property,
      meters: {
        ...combinedData.property.meters,
        edges: filteredEdges,
      },
    },
  };
};

/**
 * Merge two sorted time-series arrays into one with unique timestamps.
 * If both arrays contain a point at the same timestamp, the point from
 * latestData (the newer segment) is kept and the one from existingData
 * is discarded.
 * @param {Array} existingData - Existing data array (points with
 *   `timestamp`).
 * @param {Array} latestData - Newer data array to merge in.
 * @returns {Array} Merged array with unique timestamps, sorted ascending.
 */
export const mergeUniqueTimeSeries = (existingData, latestData) => {
  const merged = [...(existingData || []), ...(latestData || [])];
  const seen = new Map();
  merged.forEach((point) => {
    seen.set(point.timestamp, point);
  });
  return Array.from(seen.values())
    .sort((a, b) => a.timestamp - b.timestamp);
};

/**
 * Build a composite key string for a trade set summary entry.
 * @param {object} key - The trade summary key object.
 * @returns {string} Composite key for map lookups.
 */
export const buildTradeKey = (key) => {
  const k = key || {};
  const {
    direction, ruleId, tradePointId, type,
  } = k;
  return `${tradePointId}_${ruleId}_${direction}_${type}`;
};

/**
 * Merge two trade summary data arrays into one with unique `range.finish`
 * values. If both arrays contain an entry with the same finish, the entry
 * from latestData (the newer segment) is kept and the one from
 * existingData is discarded.
 * @param {Array} existingData - Existing data array.
 * @param {Array} latestData - Newer data array to merge in.
 * @returns {Array} Merged array with unique range.finish, sorted ascending.
 */
export const mergeUniqueTradeSummaryData = (existingData, latestData) => {
  const merged = [...(existingData || []), ...(latestData || [])];
  const seen = new Map();
  merged.forEach((point) => {
    const finish = point?.range?.finish;
    if (finish != null) {
      seen.set(finish, point);
    }
  });
  return Array.from(seen.values())
    .sort((a, b) => (a?.range?.finish ?? 0) - (b?.range?.finish ?? 0));
};

/**
 * Merge two combinedData structures produced by the PropertyShow data
 * pipeline. Combines meter-level time-series arrays, keeping only unique
 * data points. When both structures contain a point at the same timestamp
 * (or trade range), the point from latestData (the newer segment) is
 * kept.
 * Meters present in only one structure are carried through unchanged.
 * @param {object} existingData - Existing combinedData
 *   `{ property: { meters: { edges: [{ node }] } } }`.
 * @param {object} latestData - Newer combinedData to merge in
 *   (same shape).
 * @returns {object} New combinedData with merged meter data.
 */
export const mergeCombinedData = (existingData, latestData) => {
  const existingEdges = existingData?.property?.meters?.edges;
  if (!existingEdges) return latestData;

  const latestEdges = latestData?.property?.meters?.edges;
  if (!latestEdges) return existingData;

  // Index latestData meters by uuid for O(1) lookup
  const latestByUuid = new Map();
  latestEdges.forEach((edge) => {
    if (edge?.node?.uuid) {
      latestByUuid.set(edge.node.uuid, edge.node);
    }
  });

  // Track which latestData meters were matched so we can append
  // unmatched ones
  const matchedUuids = new Set();

  const mergedEdges = existingEdges.map((edge) => {
    const existingNode = edge?.node;
    if (!existingNode?.uuid) return edge;

    const latestNode = latestByUuid.get(existingNode.uuid);
    if (!latestNode) return edge;

    matchedUuids.add(existingNode.uuid);

    // Merge consumed
    const mergedConsumed = {
      ...(existingNode[DATA_CONSUMED_KEY] || {}),
      ...(latestNode[DATA_CONSUMED_KEY] || {}),
      data: mergeUniqueTimeSeries(
        existingNode[DATA_CONSUMED_KEY]?.data,
        latestNode[DATA_CONSUMED_KEY]?.data,
      ),
    };

    // Merge generated
    const mergedGenerated = {
      ...(existingNode[DATA_GENERATED_KEY] || {}),
      ...(latestNode[DATA_GENERATED_KEY] || {}),
      data: mergeUniqueTimeSeries(
        existingNode[DATA_GENERATED_KEY]?.data,
        latestNode[DATA_GENERATED_KEY]?.data,
      ),
    };

    // Merge trade set summary
    const existingTradeMap = new Map();
    (existingNode.tradeSetSummary || []).forEach((trade) => {
      existingTradeMap.set(buildTradeKey(trade.key), trade);
    });

    const mergedTrades = [
      ...(existingNode.tradeSetSummary || []),
    ];
    (latestNode.tradeSetSummary || []).forEach((trade) => {
      const tradeKey = buildTradeKey(trade.key);
      const existingTrade = existingTradeMap.get(tradeKey);
      if (existingTrade) {
        const idx = mergedTrades.indexOf(existingTrade);
        mergedTrades[idx] = {
          ...existingTrade,
          data: mergeUniqueTradeSummaryData(
            existingTrade.data,
            trade.data,
          ),
        };
      } else {
        mergedTrades.push(trade);
      }
    });

    // Merge rules (dedup by uuid)
    const mergedRulesEdges = [
      ...(existingNode.rules?.edges || []),
    ];
    const existingRuleIds = new Set(
      mergedRulesEdges.map((e) => e?.node?.uuid).filter(Boolean),
    );
    (latestNode.rules?.edges || []).forEach((ruleEdge) => {
      if (
        ruleEdge?.node?.uuid
        && !existingRuleIds.has(ruleEdge.node.uuid)
      ) {
        mergedRulesEdges.push(ruleEdge);
      }
    });

    return {
      node: {
        ...existingNode,
        ...latestNode,
        [DATA_CONSUMED_KEY]: mergedConsumed,
        [DATA_GENERATED_KEY]: mergedGenerated,
        tradeSetSummary: mergedTrades,
        rules: { edges: mergedRulesEdges },
      },
    };
  });

  // Append meters that exist only in latestData
  latestEdges.forEach((edge) => {
    if (edge?.node?.uuid && !matchedUuids.has(edge.node.uuid)) {
      mergedEdges.push(edge);
    }
  });

  return {
    ...existingData,
    property: {
      ...existingData.property,
      ...latestData.property,
      meters: {
        ...existingData.property.meters,
        edges: mergedEdges,
      },
    },
  };
};

/**
 * Store a fetch result as a cache segment. Extracts meters and rulesData
 * from the mergedData. Before inserting, checks for existing segments
 * with the same aggregation that are adjacent to or overlap with the
 * new data. If found, merges the combinedData and expands the date range
 * to the union, replacing the old segment(s).
 * Evicts the oldest segment (FIFO) when at capacity
 * ({@link MAX_CACHE_SEGMENTS}).
 * Mutates `cache.segments` in place — designed to work with React refs
 * so the caller does not need to clone the cache on every insert.
 * @param {object} cacheObj - The cache object (with `segments`).
 * @param {object} span - The date range with `start` and `finish` Luxon DateTimes.
 * @param {object} mergedData - The combinedData to cache.
 * @param {string} aggregation - Aggregation level (e.g. 'PT30M', 'P1D').
 */
export const addSegmentToCache = (cacheObj, span, mergedData, aggregation) => {
  if (!mergedData?.property?.meters?.edges) return;

  const meters = mergedData.property.meters.edges
    .map((edge) => edge.node);
  const rulesData = meters.map((meter) => ({
    uuid: meter.uuid,
    rules: meter.rules,
  }));

  const { segments } = cacheObj;

  let finalStart = span.start;
  let finalFinish = span.finish;
  let finalData = mergedData;
  let finalMeters = meters;
  let finalRulesData = rulesData;

  // Find and merge with any adjacent/overlapping same-aggregation
  // segments. Loop until no more mergeable segments remain (chain
  // merge: A+B then C adjacent to merged AB).
  let merged = true;
  while (merged) {
    merged = false;
    const keys = Object.keys(segments);
    for (let i = 0; i < keys.length; i += 1) {
      const seg = segments[keys[i]];
      if (seg.aggregation !== aggregation) {
        // eslint-disable-next-line no-continue
        continue;
      }

      const segStartSec = seg.start.toSeconds();
      const segFinishSec = seg.finish.toSeconds();
      const newStartSec = finalStart.toSeconds();
      const newFinishSec = finalFinish.toSeconds();

      // Adjacent: gap between segments is ≤1 s (handles endOf
      // month .999 vs startOf next month .000 millisecond gap).
      // Overlapping: ranges intersect.
      const gapAfterSeg = newStartSec - segFinishSec;
      const gapAfterNew = segStartSec - newFinishSec;
      const adjacent = (gapAfterSeg >= 0 && gapAfterSeg <= 1)
        || (gapAfterNew >= 0 && gapAfterNew <= 1);
      const overlapping = segStartSec < newFinishSec
        && newStartSec < segFinishSec;

      if (adjacent || overlapping) {
        finalData = mergeCombinedData(seg.combinedData, finalData);
        finalStart = segStartSec < newStartSec
          ? seg.start : finalStart;
        finalFinish = segFinishSec > newFinishSec
          ? seg.finish : finalFinish;

        // Prefer the longer meters list
        if (seg.meters.length > finalMeters.length) {
          finalMeters = seg.meters;
          finalRulesData = seg.rulesData;
        }

        delete segments[keys[i]];
        merged = true;
        break; // restart loop after mutation
      }
    }
  }

  const startSec = Math.floor(finalStart.toSeconds());
  const finishSec = Math.floor(finalFinish.toSeconds());
  const segmentKey = `${startSec}_${finishSec}`;

  if (Object.keys(segments).length >= MAX_CACHE_SEGMENTS) {
    delete segments[Object.keys(segments)[0]];
  }

  segments[segmentKey] = {
    start: finalStart,
    finish: finalFinish,
    combinedData: finalData,
    meters: finalMeters,
    rulesData: finalRulesData,
    aggregation,
    lastUpdatedAt: Date.now(),
  };

  logger.log(
    'Cached segment:',
    segmentKey,
    'with',
    finalMeters.length,
    'meters',
  );
};

// ── High-level Cache Layer API ──────────────────────────────────
// Consumers (hooks, components) should call only these functions.
// The functions above are internal helpers used by this layer.

/**
 * Look up a cached segment that covers the requested dateSpan and
 * aggregation level. If found, extracts and returns the data scoped
 * to the requested range along with the cached meters and rulesData.
 * @param {object} cacheObj - The cache object.
 * @param {object} dateSpan - Requested range with `start` and `finish`
 *   Luxon DateTimes.
 * @param {string} aggregation - Required aggregation level
 *   (e.g. 'PT30M', 'P1D').
 * @returns {object|null} `{ data, meters, rulesData }` on hit, or
 *   `null` on miss.
 */
export const lookupCache = (cacheObj, dateSpan, aggregation) => {
  const segment = getSegmentDataFromCache(cacheObj.segments, dateSpan, aggregation);
  if (!segment) return null;
  return {
    data: extractDataFromCache(segment.combinedData, dateSpan),
    meters: segment.meters,
    rulesData: segment.rulesData,
  };
};

/**
 * Read-through cache: looks up the cache for the requested date range
 * and aggregation. On a cache hit, calls onDataFunc immediately with
 * the cached data. On a miss, delegates to getPropertyDataInBatches,
 * stores the result, then calls onDataFunc.
 *
 * ## Cache bypass
 * To skip caching entirely, import and call `getPropertyDataInBatches`
 * (from `./dataBatching`) directly instead of this function. No other
 * code changes are needed.
 * @param {object} params - Function parameters.
 * @param {string} params.propertyId - The property UUID. When the
 *   property changes the module-level cache is automatically reset.
 * @param {object} params.dateSpan - { start, finish } Luxon DateTimes.
 * @param {string} params.aggregation - e.g. 'PT30M', 'P1D'.
 * @param {object} params.fetchParams - Extra params forwarded to
 *   getPropertyDataInBatches (propertyId, timezone, environment, etc.).
 * @param {Function} params.onDataFunc - Called with
 *   (data, meters, rulesData, fromCache) on both hit and miss.
 * @param {Function} params.onErrorFunc - (error, message) => void.
 * @param {Function} [params.onBeforeFetchFunc] - Called on cache miss
 *   before the fetch starts. Receives ({ segmentsExist }).
 * @param {object} params.isFetchingRef - Ref tracking in-flight state.
 * @param {object} params.cancelledRef - Ref for cancellation.
 * @param {Function} [params.stateDispatchFunc] - Passed through to
 *   fetchFunc for batch progress dispatches.
 */
export const getPropertyDataFromCache = ({
  propertyId,
  dateSpan,
  aggregation,
  fetchParams,
  onDataFunc,
  onErrorFunc,
  onBeforeFetchFunc,
  isFetchingRef,
  cancelledRef,
  stateDispatchFunc,
}) => {
  // Reset cache when propertyId changes
  if (cache.propertyId !== propertyId) {
    cache = initialiseCache(propertyId);
  }

  // 1. Check cache
  const cacheHit = lookupCache(cache, dateSpan, aggregation);
  if (cacheHit) {
    logger.log('Cache hit - serving data from cached segment');
    onDataFunc(cacheHit.data, cacheHit.meters, cacheHit.rulesData, true);
    return;
  }

  // 2. Cache miss — notify caller, then fetch
  logger.log(
    'Cache miss - fetching data for date range:',
    dateSpan.start.toISODate(),
    'to',
    dateSpan.finish.toISODate(),
  );

  const segmentsExist = Object.keys(cache.segments).length > 0;

  if (onBeforeFetchFunc) {
    onBeforeFetchFunc({ segmentsExist });
  }

  // 3. Delegate to getPropertyDataInBatches
  getPropertyDataInBatches({
    ...fetchParams,
    dateSpan,
    historianAggregation: aggregation,
    isFetchingRef,
    cancelledRef,
    stateDispatchFunc,
    onDataReceived: (mergedData) => {
      addSegmentToCache(cache, dateSpan, mergedData, aggregation);
      const meters = mergedData?.property?.meters?.edges
        ?.map((edge) => edge.node) || [];
      const rulesData = meters.map((meter) => ({
        uuid: meter.uuid,
        rules: meter.rules,
      }));
      onDataFunc(mergedData, meters, rulesData, false);
    },
    onError: onErrorFunc,
  });
};
